import { reverse } from 'named-urls';
import React, { useState } from 'react';
import { NumberString, PriceString } from '@fourthwall/components';
import { createDateRangeParams, formatMoneyValue, formatNumberValue, getDateFormatter, getValue, mapChartToSeriesData, useAnalyticsChartQuery, } from '@fourthwall/module-analytics';
import { useShopTime } from '@modules/Timezone/providers/ShopTimeProvider';
import { routing } from '@utils/routing';
import { useCurrentShop } from '../../../../hooks/useCurrentShop';
const displayValue = (value) => {
    if (value === undefined) {
        return 0;
    }
    if (typeof value === 'object') {
        return React.createElement(PriceString, { value: value.value });
    }
    if (typeof value === 'string') {
        return value;
    }
    return React.createElement(NumberString, { value: value });
};
const BASE_OPTIONS = [
    { label: 'Today', value: 'today' },
    { label: 'Last 7 days', value: 'last7days' },
    { label: 'Last 30 days', value: 'last30days' },
    { label: 'Last 90 days', value: 'last90days' },
];
export const useHomeAnalytics = () => {
    const { getCurrentShopTime, getCurrentRelativeShopTime, timezone } = useShopTime();
    const dateRangeParams = createDateRangeParams(getCurrentShopTime());
    const options = BASE_OPTIONS.map((option) => ({
        ...option,
        params: dateRangeParams[option.value],
    }));
    const [tabsValue, setTabsValue] = useState(options[2].value);
    const { currentShop } = useCurrentShop({ staleTime: Infinity });
    const option = options.find((option) => option.value === tabsValue);
    const optionParams = option?.params;
    const params = {
        from: '',
        to: '',
        precision: tabsValue === 'today' ? 'HOUR' : 'DAY',
        sid: [currentShop?.id],
        chartTypes: ['PROFIT', 'CONTRIBUTION', 'CONTRIBUTION_AVG', 'GROSS_SALES'],
        ...optionParams,
    };
    const { analyticsChartQuery } = useAnalyticsChartQuery([{ ...params, zoneId: timezone }], {
        staleTime: 60_000, // 1 minute,
    });
    const hasValues = !!analyticsChartQuery.data?.current.profit?.aggregatedValue ||
        !!analyticsChartQuery.data?.current.contribution?.aggregatedValue ||
        !!analyticsChartQuery.data?.current.contribution?.aggregatedValue;
    const createTo = (reportType) => {
        return `${reverse(routing.analytics.overview.report.self, {
            reportType,
        })}?range=${tabsValue}`;
    };
    const isValidValue = (value) => {
        return options.some((option) => option.value === value);
    };
    const chartOptions = {
        futureDates: false,
        allowPoints: false,
        now: getCurrentRelativeShopTime(),
    };
    return {
        tabs: {
            options: [...options],
            value: tabsValue,
            onChange: (value) => {
                if (!isValidValue(value))
                    return;
                setTabsValue(value);
            },
        },
        stats: [
            {
                label: 'Estimated profit',
                to: createTo('profit'),
                value: analyticsChartQuery.isLoading
                    ? null
                    : displayValue(getValue(analyticsChartQuery.data?.current.profit)),
                chart: {
                    series: [
                        {
                            label: 'Estimated profit',
                            data: mapChartToSeriesData(analyticsChartQuery.data?.current.profit?.data, { ...params }, chartOptions),
                        },
                    ],
                    formatDate: getDateFormatter(params.precision, params.from, params.to),
                    formatValue: formatMoneyValue,
                },
            },
            {
                label: 'Orders',
                to: createTo('orders'),
                value: analyticsChartQuery.isLoading
                    ? null
                    : displayValue(getValue(analyticsChartQuery.data?.current.contribution)),
                chart: {
                    series: [
                        {
                            label: 'Orders',
                            data: mapChartToSeriesData(analyticsChartQuery.data?.current.contribution?.data, { ...params }, chartOptions),
                        },
                    ],
                    formatDate: getDateFormatter(params.precision, params.from, params.to),
                    formatValue: formatNumberValue,
                },
            },
            {
                label: 'Average order value',
                to: createTo('average-order-value'),
                value: analyticsChartQuery.isLoading
                    ? null
                    : displayValue(getValue(analyticsChartQuery.data?.current.averageGrossSalesValue)),
                chart: {
                    series: [
                        {
                            label: 'Average order value',
                            data: mapChartToSeriesData(analyticsChartQuery.data?.current.contributionAvg?.data, { ...params }, chartOptions),
                        },
                    ],
                    formatDate: getDateFormatter(params.precision, params.from, params.to),
                    formatValue: formatMoneyValue,
                },
            },
        ].map((stat) => {
            const getChart = () => {
                // During initial loading, return empty chart to show skeleton
                if (analyticsChartQuery.isLoading) {
                    return { series: [], formatDate: () => '', formatValue: () => '' };
                }
                // After loading, if no values, return undefined to hide chart section
                if (!hasValues) {
                    return undefined;
                }
                return stat.chart;
            };
            return {
                ...stat,
                chart: getChart(),
                loading: analyticsChartQuery.isLoading,
            };
        }),
    };
};
